import { Link } from "react-router-dom";
import { ShoppingBag } from "lucide-react";
import { useCartStore } from "@/stores/cartStore";
import { formatPriceNok, type Product } from "@/lib/shop";
import { emitFlyToCart } from "@/lib/fly-to-cart";
import { StockBadge, stockStatusOf } from "./StockBadge";

interface ProductCardProps {
  product: Product;
}

const ProductCard = ({ product }: ProductCardProps) => {
  const addItem = useCartStore((s) => s.addItem);
  const firstVariant = product.variants[0];
  const primaryImage = product.images[0];
  const stock = stockStatusOf(firstVariant);
  const soldOut = stock === "out_of_stock";

  const handleAddToCart = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!firstVariant || soldOut) return;
    addItem(product, firstVariant, 1);
    if (primaryImage) {
      emitFlyToCart({
        sourceRect: e.currentTarget.getBoundingClientRect(),
        imageUrl: primaryImage.url,
      });
    }
  };

  return (
    <Link
      to={`/produkt/${product.slug}`}
      className="group block overflow-hidden rounded-2xl bg-card shadow-sm transition-all duration-300 hover:shadow-md hover:-translate-y-1"
    >
      <div className="relative aspect-square overflow-hidden bg-secondary/30">
        {primaryImage ? (
          <img
            src={primaryImage.url}
            alt={primaryImage.alt ?? product.title}
            className={`h-full w-full object-cover transition-transform duration-500 group-hover:scale-105 ${soldOut ? "opacity-60" : ""}`}
            loading="lazy"
            width={400}
            height={400}
          />
        ) : (
          <div className="flex h-full items-center justify-center text-muted-foreground">
            <ShoppingBag className="h-12 w-12" />
          </div>
        )}
        {stock !== "in_stock" && (
          <div className="absolute left-3 top-3">
            <StockBadge status={stock} />
          </div>
        )}
      </div>
      <div className="p-5">
        <h3 className="font-heading text-lg font-bold">{product.title}</h3>
        {product.description && (
          <p className="mt-1 line-clamp-2 text-sm text-muted-foreground font-body">
            {product.description}
          </p>
        )}
        <div className="mt-4 flex items-center justify-between">
          <span className="text-lg font-bold text-primary font-body">
            {firstVariant ? formatPriceNok(firstVariant.priceOre) : "—"}
          </span>
          <button
            onClick={handleAddToCart}
            disabled={!firstVariant || soldOut}
            className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-all hover:opacity-90 hover:-translate-y-0.5 disabled:translate-y-0 disabled:opacity-50 font-body"
          >
            <ShoppingBag className="h-4 w-4" />
            {soldOut ? "Utsolgt" : "Kjøp"}
          </button>
        </div>
      </div>
    </Link>
  );
};

export default ProductCard;
